/**
 * VHAKACHA FOLLOWS  (web app)
 * ============================================================
 * Operators a traveller follows.
 *
 * Stored at `users/{uid}/follows/{providerId}` — the provider id IS the
 * document id, so "am I following this one?" is a single get rather than a
 * query, and following twice writes the same document rather than a duplicate.
 *
 * PRIVATE TO THE FOLLOWER, including from the operator being followed.
 * firestore.rules refuses every other reader, admins included. A provider able
 * to enumerate its followers would have a named marketing list built out of a
 * feature travellers read as a bookmark. Nothing here is the boundary — the
 * rules are — but the shape of this module follows the same intent.
 *
 * WHY THE PROVIDER NAME IS COPIED IN
 * ----------------------------------
 * The following page would otherwise read one `service_providers` document per
 * follow to render a list of names. The copy can go stale if an operator
 * renames itself; the page shows the name it was followed under until the
 * follow is touched again, which is a smaller problem than N reads on every
 * visit. `listings.company.name` is denormalized for the same reason.
 * ============================================================
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';

const followsRef = (uid) => collection(db, 'users', uid, 'follows');
const followRef = (uid, providerId) => doc(db, 'users', uid, 'follows', providerId);

/**
 * Is this traveller following this operator?
 *
 * Answers false for a signed-out visitor rather than throwing: the button is
 * rendered before anyone signs in, and "not following" is the honest state to
 * show them.
 */
export async function isFollowing(uid, providerId) {
  if (!uid || !providerId) return false;

  try {
    const snapshot = await getDoc(followRef(uid, providerId));
    return snapshot.exists();
  } catch (error) {
    console.error('Failed to read follow state', providerId, error);
    return false;
  }
}

/**
 * Follow an operator. Idempotent — the id is the provider's.
 *
 * @returns {Promise<boolean>} whether it was written
 */
export async function followProvider(uid, provider) {
  if (!uid || !provider?.id || !provider?.name) return false;

  try {
    await setDoc(followRef(uid, provider.id), {
      providerId: provider.id,
      // Required by the rules: a follow with no name is a row the traveller
      // cannot identify on their own following page.
      providerName: provider.name,
      createdAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error('Failed to follow', provider.id, error);
    return false;
  }
}

/** @returns {Promise<boolean>} whether it was removed */
export async function unfollowProvider(uid, providerId) {
  if (!uid || !providerId) return false;

  try {
    await deleteDoc(followRef(uid, providerId));
    return true;
  } catch (error) {
    console.error('Failed to unfollow', providerId, error);
    return false;
  }
}

/**
 * Everyone this traveller follows, most recently followed first.
 *
 * Returns [] rather than throwing on a failed read — the following page says
 * "nothing yet" instead of breaking, which is wrong in the direction that
 * still leaves a usable page.
 */
export async function getFollowedProviders(uid) {
  if (!uid) return [];

  try {
    const snapshot = await getDocs(query(followsRef(uid), orderBy('createdAt', 'desc')));

    return snapshot.docs
      .map((entry) => {
        const data = entry.data() || {};
        return {
          providerId: entry.id,
          providerName: typeof data.providerName === 'string' ? data.providerName : '',
          createdAt: data.createdAt ?? null,
        };
      })
      .filter((follow) => follow.providerName);
  } catch (error) {
    console.error('Failed to read follows', error);
    return [];
  }
}
